import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react'
import { WalletConnectV2Provider } from '@multiversx/sdk-wallet-connect-provider'
import { ExtensionProvider } from '@multiversx/sdk-extension-provider'
import { HWProvider } from '@multiversx/sdk-hw-provider'
import {
  WalletProvider,
  WALLET_PROVIDER_DEVNET,
  WALLET_PROVIDER_MAINNET,
} from '@multiversx/sdk-web-wallet-provider'
import type { PasskeyProvider } from '@shelters/sdk-passkey-provider'

const projectId = process.env.NEXT_PUBLIC_WALLET_CONNECT_PROJECT_ID
const relayUrl = 'wss://relay.walletconnect.com'
const chainId = process.env.NEXT_PUBLIC_CHAIN_ID

export interface MultiversxContextProps {
  extensionProvider?: ExtensionProvider
  webWalletProvider?: WalletProvider
  walletConnectProvider?: WalletConnectV2Provider
  passkeyProvider?: PasskeyProvider
  ledgerProvider?: HWProvider
  setWalletConnectCB: Dispatch<SetStateAction<CbType>>
}

const multiversxContext = createContext<MultiversxContextProps>(
  undefined as any,
)

interface CbType {
  onClientLogin: () => void
  onClientLogout: () => void
  onClientEvent: (event: string) => void
}

const MultiversxProvider = ({ children }: { children: any }) => {
  const [walletConnectCB, setWalletConnectCB] = useState<CbType>({
    onClientLogin: () => {},
    onClientLogout: () => {},
    onClientEvent: () => {},
  })
  const callbacks = {
    onClientLogin: async function () {
      console.log('onClientLogin')
      walletConnectCB.onClientLogin()
    },
    onClientLogout: async function () {
      console.log('onClientLogout')
      walletConnectCB.onClientLogout()
    },
    onClientEvent: async function (event: any) {
      console.log('onClientEvent', event)
      walletConnectCB.onClientEvent(event)
    },
  }
  const [extensionProvider, setExtensionProvider] = useState<ExtensionProvider>(
    ExtensionProvider.getInstance(),
  )
  const [webWalletProvider, setWebWalletProvider] = useState<WalletProvider>()
  const [walletConnectProvider, setWalletConnectProvider] =
    useState<WalletConnectV2Provider>(
      new WalletConnectV2Provider(callbacks, chainId, relayUrl, projectId, {
        logger: 'info',
      }),
    )
  const [ledgerProvider, setLedgerProvider] = useState<HWProvider>(
    new HWProvider(),
  )

  const [passkeyProvider, setPasskeyProvider] = useState()

  useEffect(() => {
    let provider: WalletProvider
    if (process.env.NEXT_PUBLIC_TARGET_ENV === 'production') {
      provider = new WalletProvider(WALLET_PROVIDER_MAINNET)
    } else {
      provider = new WalletProvider(WALLET_PROVIDER_DEVNET)
    }
    setWebWalletProvider(provider)

    if (typeof window !== 'undefined') {
      let { PasskeyProvider } = require('@shelters/sdk-passkey-provider')
      setPasskeyProvider(PasskeyProvider.getInstance())
    }
  }, [])

  // Extension MultiversX Mock for Localhost
  useEffect(() => {
    if (typeof window === 'undefined') return

    const isLocalhost =
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1'
    const hasRealExtension = Boolean(
      window.multiversxWallet || window.elrondWallet,
    )

    if (!isLocalhost || hasRealExtension) return

    const win = window as any
    const MOCK_EXTENSION_ID = 'shelters-local-mock-extension'

    win.multiversxWallet = { extensionId: MOCK_EXTENSION_ID }
  }, [])

  return (
    <multiversxContext.Provider
      value={{
        extensionProvider,
        walletConnectProvider,
        webWalletProvider,
        ledgerProvider,
        passkeyProvider,
        setWalletConnectCB,
      }}
    >
      {children}
    </multiversxContext.Provider>
  )
}

export default MultiversxProvider

export const useMultiversx = () => useContext(multiversxContext)
